// TDS → Deductees / Parties: the quarter's salary TDS, employee by
// employee, grouped on PAN.
//
// Grouped on PAN rather than on name or employee code because that is how
// Annexure I of the 24Q groups it: one deductee is one PAN, and two rows
// that share a PAN are one person however their name was typed. Rows with
// no PAN cannot be grouped that way at all, so each is kept on its own and
// counted separately — merging them on name would invent a deductee.
//
// Built from the same /tds/salary-entries the Transactions screen reads.
// A second endpoint totalling the same rows is a second place for the
// totals to be wrong.
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Upload } from "lucide-react";

import { tdsApi, TdsSalaryEntry } from "../../api/tds";
import { EMPTY_FILTERS, TdsFilters, matchesFilters } from "./TdsFilterBar";

const inr = (n: number) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const showDate = (iso: string | null) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return d && m && y ? `${d}-${m}-${y}` : iso;
};

export const DEDUCTEE_CSS = `
.tds-ded-stats { display: flex; gap: 34px; flex-wrap: wrap; margin: 14px 0 0; }
.tds-ded-stat-label { font-size: 11.5px; color: var(--gt-text-tertiary); }
.tds-ded-stat-value { font-size: 20px; font-weight: 800; margin-top: 2px; }
.tds-ded-stat-value.is-warn { color: #b91c1c; }

.tds-ded-warn {
  display: flex; gap: 10px; align-items: flex-start;
  margin-top: 16px; padding: 14px 16px; border-radius: 10px;
  background: #fef3c7; border: 1px solid #fcd34d; color: #78350f;
}
.tds-ded-warn h4 { font-size: 13px; font-weight: 700; margin: 0 0 4px; }
.tds-ded-warn p { font-size: 12px; margin: 0; line-height: 1.5; }

.tds-ded-search {
  min-width: 240px; flex: 1; max-width: 340px;
  padding: 9px 12px; font-size: 13px;
  border: 1px solid var(--gt-border); border-radius: 8px;
  background: var(--gt-surface-0);
}
.tds-ded-search:focus { outline: 2px solid var(--gt-primary); outline-offset: -1px; }

.tds-ded-pill {
  display: inline-block; padding: 2px 8px; border-radius: 999px;
  font-size: 11px; font-weight: 600; background: var(--gt-surface-2);
  color: var(--gt-text-secondary); margin-right: 4px;
}
.tds-ded-pill.is-missing {
  background: #fff; color: #b91c1c; border: 1px solid #fca5a5;
}

.tds-ded-row { cursor: pointer; }
.tds-ded-row:hover { background: var(--gt-surface-hover); }
.tds-ded-sub { background: var(--gt-surface-2); }
`;

interface Deductee {
  key: string;
  name: string;
  pan: string | null;
  code: string | null;
  sections: string[];
  entries: TdsSalaryEntry[];
  gross: number;
  tds: number;
  deposited: number;
}

/** One row per PAN; one row per entry for those that have none. */
function group(entries: TdsSalaryEntry[]): Deductee[] {
  const byKey = new Map<string, Deductee>();
  entries.forEach((entry, index) => {
    // No PAN means no way to tell two people apart, so each such row stays
    // on its own rather than being folded in by name.
    const key = entry.pan ? `pan:${entry.pan}` : `row:${entry.id || index}`;
    let row = byKey.get(key);
    if (!row) {
      row = {
        key,
        name: entry.employee_name,
        pan: entry.pan,
        code: entry.employee_code,
        sections: [],
        entries: [],
        gross: 0,
        tds: 0,
        deposited: 0,
      };
      byKey.set(key, row);
    }
    row.entries.push(entry);
    row.gross += Number(entry.amount_paid || 0);
    row.tds += Number(entry.total || 0);
    // Deposited only when a PAID challan reports it. A draft challan is a
    // plan, not a payment.
    if (entry.deposited) row.deposited += Number(entry.total || 0);
    if (entry.section && !row.sections.includes(entry.section)) row.sections.push(entry.section);
  });
  return [...byKey.values()].sort((a, b) => b.tds - a.tds);
}

const rateOf = (row: { gross: number; tds: number }) =>
  row.gross > 0 ? `${((row.tds / row.gross) * 100).toFixed(2)}%` : "—";

export default function TdsDeductees({
  financialYear,
  quarter,
  filters = EMPTY_FILTERS,
}: {
  financialYear: string;
  quarter: number;
  filters?: TdsFilters;
}) {
  const [entries, setEntries] = useState<TdsSalaryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    tdsApi.salaryEntries(financialYear, quarter)
      .then((r) => { if (alive) { setEntries(r.data.entries); setError(null); } })
      .catch((e) => {
        if (!alive) return;
        setEntries([]);
        setError(e?.response?.data?.detail || "Couldn't load this quarter's deductees.");
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [financialYear, quarter]);

  // Narrowed BEFORE grouping, so a deductee's row adds up only the
  // entries on screen. Grouping first and filtering the groups after
  // would show a person's whole-quarter total under a single month.
  const all = useMemo(
    () => group(entries.filter((e) => matchesFilters(e, filters))),
    [entries, filters]);

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return all;
    return all.filter((row) =>
      row.name.toLowerCase().includes(needle)
      || (row.pan || "").toLowerCase().includes(needle)
      || (row.code || "").toLowerCase().includes(needle)
      || row.sections.some((s) => s.includes(needle)));
  }, [all, search]);

  const withoutPan = all.filter((row) => !row.pan).length;
  const totalTds = all.reduce((sum, row) => sum + row.tds, 0);

  const onExport = () => {
    if (!rows.length) { toast.error("Nothing to export yet."); return; }
    const header = ["Employee", "PAN", "Code", "Sections", "Entries", "Gross Paid", "TDS", "Rate"];
    const body = rows.map((row) => [
      row.name, row.pan || "", row.code || "", row.sections.join(" "),
      row.entries.length, row.gross, row.tds, rateOf(row),
    ]);
    const csv = [header, ...body]
      .map((line) => line.map((cell) => {
        const text = String(cell ?? "");
        return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
      }).join(","))
      .join("\r\n");
    const url = URL.createObjectURL(new Blob([`﻿${csv}`], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `TDS_deductees_${financialYear}_Q${quarter}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    toast.success(`${rows.length} deductee${rows.length === 1 ? "" : "s"} exported`);
  };

  return (
    <div className="gt-card p-5">
      <style>{DEDUCTEE_CSS}</style>

      <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <span className="gt-eyebrow">Deductees / Parties (Q{quarter} FY {financialYear})</span>
          <p className="gt-page-sub" style={{ fontSize: 12, margin: "4px 0 0" }}>
            The quarter's salary TDS employee by employee, grouped on PAN — the way
            Annexure I of the 24Q groups them. Click a row for its entries.
          </p>
        </div>
        <input
          className="tds-ded-search"
          placeholder="Search by name, PAN, code or section"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button type="button" className="gt-action-btn is-ghost tds-tx-btn"
          onClick={onExport} disabled={!rows.length}>
          <Upload /> Export
        </button>
      </div>

      <div className="tds-ded-stats">
        <div>
          <div className="tds-ded-stat-label">Deductees</div>
          <div className="tds-ded-stat-value">{all.length}</div>
        </div>
        <div>
          <div className="tds-ded-stat-label">Without PAN</div>
          <div className={`tds-ded-stat-value${withoutPan ? " is-warn" : ""}`}>{withoutPan}</div>
        </div>
        <div>
          <div className="tds-ded-stat-label">Total TDS</div>
          <div className="tds-ded-stat-value">{inr(totalTds)}</div>
        </div>
      </div>

      {withoutPan > 0 && (
        <div className="tds-ded-warn">
          <span aria-hidden style={{ fontSize: 16, lineHeight: 1.2 }}>⚠</span>
          <div>
            <h4>{withoutPan} deductee{withoutPan === 1 ? "" : "s"} without a PAN</h4>
            <p>
              The statement takes PANNOTAVBL in place of a PAN, so these will still
              file. But 206AA puts the deduction at 20%, and the employee gets no
              credit in their 26AS for tax you did deduct. Set the PAN before you file.
            </p>
          </div>
        </div>
      )}

      {error && <p style={{ color: "#b91c1c", fontSize: 12.5, marginTop: 14 }}>{error}</p>}

      <div style={{ overflow: "auto", marginTop: 16 }}>
        <table style={{ width: "100%", minWidth: 820, fontSize: 12 }}>
          <thead>
            <tr style={{ background: "var(--gt-surface-2)" }}>
              <th style={{ width: 28 }} />
              {["Employee", "PAN", "Sections"].map((h) => (
                <th key={h} style={{ textAlign: "left", padding: 8, whiteSpace: "nowrap" }}>{h}</th>
              ))}
              {["Entries", "Gross Paid", "TDS", "Rate", "Deposited", "Pending"].map((h) => (
                <th key={h} style={{ textAlign: "right", padding: 8, whiteSpace: "nowrap" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!rows.length && (
              <tr>
                <td colSpan={10} style={{ padding: 20, textAlign: "center", color: "var(--gt-text-tertiary)" }}>
                  {/* Three different reasons for an empty table, and they
                      ask for three different things: a wider search, a
                      wider filter, or an import. */}
                  {loading ? "Loading…"
                    : all.length ? "Nothing matches that search."
                      : entries.length ? "Nothing matches the month and section above."
                        : "No deductees yet — import the quarter's transactions first."}
                </td>
              </tr>
            )}
            {rows.map((row) => {
              const expanded = open === row.key;
              return [
                <tr key={row.key} className="tds-ded-row"
                  style={{ borderTop: "1px solid var(--gt-border)" }}
                  onClick={() => setOpen(expanded ? null : row.key)}>
                  <td style={{ padding: 8, color: "var(--gt-text-tertiary)" }}>
                    {expanded
                      ? <ChevronDown style={{ width: 14, height: 14 }} />
                      : <ChevronRight style={{ width: 14, height: 14 }} />}
                  </td>
                  <td style={{ padding: 8 }}>{row.name}</td>
                  <td style={{ padding: 8, fontFamily: "var(--gt-font-mono)" }}>
                    {row.pan || <span className="tds-ded-pill is-missing">missing</span>}
                  </td>
                  <td style={{ padding: 8 }}>
                    {row.sections.map((s) => <span key={s} className="tds-ded-pill">{s}</span>)}
                  </td>
                  <td style={{ padding: 8, textAlign: "right" }}>{row.entries.length}</td>
                  <td style={{ padding: 8, textAlign: "right" }}>{inr(row.gross)}</td>
                  <td style={{ padding: 8, textAlign: "right", fontWeight: 600 }}>{inr(row.tds)}</td>
                  <td style={{ padding: 8, textAlign: "right" }}>{rateOf(row)}</td>
                  <td style={{ padding: 8, textAlign: "right" }}>{inr(row.deposited)}</td>
                  <td style={{ padding: 8, textAlign: "right",
                    color: row.tds - row.deposited > 0 ? "#b45309" : undefined, fontWeight: 600 }}>
                    {inr(row.tds - row.deposited)}
                  </td>
                </tr>,
                expanded && (
                  <tr key={`${row.key}-open`} className="tds-ded-sub">
                    <td />
                    <td colSpan={9} style={{ padding: "4px 8px 12px" }}>
                      <table style={{ width: "100%", fontSize: 11.5 }}>
                        <thead>
                          <tr style={{ color: "var(--gt-text-tertiary)" }}>
                            {["Date", "Code", "Section"].map((h) => (
                              <th key={h} style={{ textAlign: "left", padding: 6, fontWeight: 600 }}>{h}</th>
                            ))}
                            {["Gross", "TDS", "Rate", "Challan"].map((h) => (
                              <th key={h} style={{ textAlign: "right", padding: 6, fontWeight: 600 }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {row.entries.map((entry) => (
                            <tr key={entry.id}>
                              <td style={{ padding: 6 }}>{showDate(entry.paid_on)}</td>
                              <td style={{ padding: 6 }}>{entry.employee_code || "—"}</td>
                              <td style={{ padding: 6 }}>{entry.section}</td>
                              <td style={{ padding: 6, textAlign: "right" }}>{inr(entry.amount_paid)}</td>
                              <td style={{ padding: 6, textAlign: "right" }}>{inr(entry.total)}</td>
                              <td style={{ padding: 6, textAlign: "right" }}>
                                {rateOf({ gross: entry.amount_paid, tds: entry.total })}
                              </td>
                              <td style={{ padding: 6, textAlign: "right" }}>
                                {entry.challan
                                  ? (entry.challan.challan_serial_no
                                    || (entry.deposited ? "paid" : "draft"))
                                  : <span style={{ color: "#b45309" }}>not deposited</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                ),
              ];
            })}
          </tbody>
        </table>
      </div>

    </div>
  );
}
